import {
  ACTIVE_PERMISSIONS,
  formatDeviceId,
  type AccessPermission,
  type AssignableRole,
  type DeviceInfo,
  type GroupInfo,
  type MemberAccess,
  type MemberInfo,
  type OrganizationDetail,
} from '@viewer/protocol';
import { useEffect, useState } from 'react';
import { api, errorText } from '../../lib/api/client';
import { useApi } from '../../lib/api/useApi';
import { Modal } from '../../ui/Modal';
import { permissionLabel, roleLabel } from './labels';
import styles from './org.module.css';

interface Props {
  org: OrganizationDetail;
  member: MemberInfo;
  onClose(): void;
  onSaved(): void;
}

const PERMISSION_ORDER: AccessPermission[] = ['view', 'control', 'file_transfer', 'clipboard', 'audio'];

function toggle<T>(list: T[], item: T, on: boolean): T[] {
  return on ? [...new Set([...list, item])] : list.filter((x) => x !== item);
}

export function MemberAccessDialog({ org, member, onClose, onSaved }: Props) {
  const current = useApi<MemberAccess>(`/api/orgs/${org.id}/members/${member.userId}/access`);
  const groups = useApi<GroupInfo[]>(`/api/orgs/${org.id}/groups`);
  const devices = useApi<DeviceInfo[]>(`/api/orgs/${org.id}/devices`);

  const [role, setRole] = useState<AssignableRole>(member.role === 'admin' ? 'admin' : 'member');
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [deviceIds, setDeviceIds] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<AccessPermission[]>(['view']);
  const [allowUnattended, setAllowUnattended] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!current.data) return;
    setGroupIds(current.data.groupIds);
    setDeviceIds(current.data.deviceIds);
    setPermissions(current.data.permissions.length ? current.data.permissions : ['view']);
    setAllowUnattended(current.data.allowUnattended);
  }, [current.data]);

  function setPermission(p: AccessPermission, on: boolean) {
    setPermissions((prev) => {
      let next = toggle(prev, p, on);
      // Controlar exige visualizar; tirar visualizar tira tudo.
      if (p === 'control' && on) next = toggle(next, 'view', true);
      if (p === 'view' && !on) next = [];
      return next;
    });
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      if (role !== member.role) await api.patch(`/api/orgs/${org.id}/members/${member.userId}`, { role });
      await api.put(`/api/orgs/${org.id}/members/${member.userId}/access`, { groupIds, deviceIds, permissions, allowUnattended });
      onSaved();
      onClose();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const grantsNothing = groupIds.length === 0 && deviceIds.length === 0;

  return (
    <Modal title={`Permissões de ${member.name}`} eyebrow={member.email} onClose={onClose} wide>
      {!current.data || !groups.data || !devices.data ? (
        <p className={styles.muted}>Carregando...</p>
      ) : (
        <div className={styles.form}>
          {org.role === 'owner' && (
            <label className={styles.field}>
              <span className={styles.label}>Função</span>
              <select className={styles.select} value={role} onChange={(e) => setRole(e.target.value as AssignableRole)}>
                <option value="member">{roleLabel.member}</option>
                <option value="admin">{roleLabel.admin}</option>
              </select>
              <span className={styles.muted}>
                {role === 'admin'
                  ? 'Organiza grupos e dispositivos e gerencia membros, mas só acessa os computadores liberados abaixo.'
                  : 'Acessa somente os computadores liberados abaixo.'}
              </span>
            </label>
          )}

          <fieldset className={styles.field} style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend className={styles.label}>Grupos liberados</legend>
            {groups.data.length === 0 && <span className={styles.muted}>Nenhum grupo criado ainda.</span>}
            {groups.data.map((g) => (
              <label key={g.id} className={styles.check}>
                <input
                  type="checkbox"
                  checked={groupIds.includes(g.id)}
                  onChange={(e) => setGroupIds((prev) => toggle(prev, g.id, e.target.checked))}
                />
                {g.name}
                <span className={styles.muted}>({g.deviceCount})</span>
              </label>
            ))}
          </fieldset>

          <fieldset className={styles.field} style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend className={styles.label}>Computadores avulsos</legend>
            <span className={styles.muted}>Para liberar um computador específico sem liberar o grupo inteiro.</span>
            {devices.data.length === 0 && <span className={styles.muted}>Nenhum computador vinculado.</span>}
            {devices.data.map((d) => (
              <label key={d.id} className={styles.check}>
                <input
                  type="checkbox"
                  checked={deviceIds.includes(d.id)}
                  onChange={(e) => setDeviceIds((prev) => toggle(prev, d.id, e.target.checked))}
                />
                {d.name}
                <span className={styles.muted}>
                  {formatDeviceId(d.code)}
                  {d.groupName && ` · ${d.groupName}`}
                </span>
              </label>
            ))}
          </fieldset>

          <fieldset className={styles.field} style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend className={styles.label}>O que pode fazer</legend>
            {PERMISSION_ORDER.map((p) => {
              const active = ACTIVE_PERMISSIONS.includes(p);
              return (
                <label key={p} className={`${styles.check} ${active ? '' : styles.checkDisabled}`}>
                  <input
                    type="checkbox"
                    disabled={!active}
                    checked={permissions.includes(p)}
                    onChange={(e) => setPermission(p, e.target.checked)}
                  />
                  {permissionLabel[p]}
                  {!active && <span className={styles.muted}>(em breve)</span>}
                </label>
              );
            })}
          </fieldset>

          <fieldset className={styles.field} style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend className={styles.label}>Acesso sem supervisão</legend>
            <label className={styles.check}>
              <input type="checkbox" checked={allowUnattended} onChange={(e) => setAllowUnattended(e.target.checked)} />
              Permitir
            </label>
            <span className={styles.muted}>
              Entra sem ninguém aceitar, mas só nos computadores cujo cliente liberou o acesso sem supervisão para a
              empresa. Quem libera é sempre o cliente, no próprio computador.
            </span>
          </fieldset>

          {grantsNothing && <p className={styles.muted}>Sem grupos ou computadores marcados, esta pessoa não acessa nada.</p>}
          {error && <p className={styles.error}>{error}</p>}
          <div className={styles.actions}>
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onClose} disabled={busy}>
              Cancelar
            </button>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => void save()} disabled={busy}>
              {busy ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
